import { ErrorBoundary } from '@sentry/react';
import { ErrorView } from '../ErrorView';
import { Link } from 'react-aria-components';
import { BadgeList } from '../BadgeList';
import { buildPractitionerBookingUrl } from '~/utilities';
import { PractitionerPhoto } from '../PractitionerPhoto';
import { SkeletonBar, Text } from '@janeapp/burrito-design-system';
import { LocationPractitioner } from '~/api';
import { useLocationPractitioners } from '~/queries';
import { analytics, useAnalytics } from '~/hooks';

export interface PractitionersAtLocationProps {
  clinicLocationGuid: string;
  clinicName: string;
  marketplacePractitionerLocationId: string;
  clinicBookingUrl?: string;
}

export const PractitionersAtLocation = ({
  clinicLocationGuid,
  clinicName,
  marketplacePractitionerLocationId,
  clinicBookingUrl,
}: PractitionersAtLocationProps) => {
  const { data, isLoading, isError, refetch } =
    useLocationPractitioners(clinicLocationGuid);
  useAnalytics();
  const rudderstackSessionId = analytics?.getSessionId()?.toString() || '';

  if (isLoading) {
    return (
      <div data-testid="skeleton-loader" className="mx-4 flex flex-col">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border-box flex flex-row gap-3 py-3">
            <SkeletonBar size="md" className="h-[56px] w-[56px] rounded-full" />
            <div className="flex flex-1 flex-col justify-center gap-1">
              <SkeletonBar size="md" className="w-2/5 md:w-3/5" />
              <SkeletonBar size="md" className="md:w-12/5 w-3/5 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (
    data?.length === 1 &&
    data[0].marketplacePractitionerLocationId ===
      marketplacePractitionerLocationId
  ) {
    return null;
  }

  return (
    <>
      <Text className="p-4 pb-2 text-xl font-semibold">
        Other practitioners at {clinicName}
      </Text>
      <ErrorBoundary
        fallback={() => (
          <ErrorView
            userMessage={`Something went wrong while loading practitioners at ${clinicName}.`}
            message={`getPractitionerAtLocation() failed. clinicLocationGuid: ${clinicLocationGuid}`}
            onRetry={refetch}
          />
        )}
      >
        <ErrorCheck
          error={isError}
          clinicLocationGuid={clinicLocationGuid}
          marketplacePractitionerLocationId={marketplacePractitionerLocationId}
          data={data}
        >
          <div
            data-testid="clinic-location-practitioners"
            className="flex flex-col gap-4 p-4"
          >
            <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
              {data
                ?.filter(
                  (practitioner) =>
                    practitioner.marketplacePractitionerLocationId !==
                    marketplacePractitionerLocationId
                )
                .map(({ photo, staffMemberGuid, fullName, disciplines }) => (
                  <div
                    className="grid grid-cols-[auto_2fr] items-center gap-3"
                    key={staffMemberGuid}
                  >
                    <PractitionerPhoto
                      photo={photo}
                      size="sm"
                      name={fullName}
                    />
                    <div className="flex flex-col">
                      <Link
                        href={buildPractitionerBookingUrl(
                          staffMemberGuid,
                          clinicBookingUrl,
                          rudderstackSessionId
                        )}
                        target="_blank"
                        aria-label={`Book an appointment with ${fullName}`}
                        className="focus-ring mb-2 cursor-pointer text-md font-semibold text-brand-80 underline"
                      >
                        {fullName}
                      </Link>
                      <BadgeList
                        id="practitioner-detail-list-disciplines"
                        badges={disciplines}
                        className="mb-2"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </ErrorCheck>
      </ErrorBoundary>
    </>
  );
};

const ErrorCheck = ({
  error,
  data,
  clinicLocationGuid,
  children,
}: {
  error: boolean;
  data?: LocationPractitioner[];
  clinicLocationGuid: string;
  marketplacePractitionerLocationId: string;
  children: React.ReactNode;
}) => {
  //throw error if no practitioners are found
  //there should always be at least one practitioner at a clinic location
  //ie. the one that is currently selected
  if (!data?.length) {
    throw new Error(
      `Didn't find any practitioners for clinicLocationGuid: ${clinicLocationGuid}`
    );
  }

  if (error) {
    throw new Error(
      `getPractitionerAtLocation() failed. clinicLocationGuid: ${clinicLocationGuid}`
    );
  }

  return <>{children}</>;
};
